import type { Dictionary } from '../types.js';

/**
 * Persian (فارسی) dictionary. RTL.
 *
 * Written as modern Iranian product copy, not translated English: conversational
 * verb forms (می‌تونی / بزن / آپلود کن) rather than bureaucratic ones, and the
 * loanwords Iranian users actually type.
 *
 * Terminology (keep consistent — do not alternate):
 *   sticker → استیکر · sticker pack → پک استیکر · image → عکس · video → ویدیو
 *   mixed → ترکیبی · upload → آپلود · project → پروژه · premium → پرمیوم
 *   settings → تنظیمات · guide → راهنما · channel → کانال · app → اپ / مینی‌اپ
 * The brand stays Latin ("Astral"), as Iranian apps normally keep it.
 *
 * All strings are stored in normal logical Unicode order — never reversed.
 */
export const fa: Dictionary = {
  common: {
    appName: 'Astral Studio',
    save: 'ذخیره',
    saving: 'در حال ذخیره…',
    saved: 'ذخیره شد',
    cancel: 'لغو',
    delete: 'حذف',
    edit: 'ویرایش',
    create: 'ساخت',
    duplicate: 'کپی',
    search: 'جستجو',
    loading: 'در حال بارگذاری…',
    back: 'بازگشت',
    confirm: 'تأیید',
    close: 'بستن',
    retry: 'تلاش دوباره',
    comingSoon: 'به‌زودی',
    optional: 'اختیاری',
    add: 'افزودن',
    remove: 'حذف',
    done: 'تمام',
    archive: 'بایگانی',
    restore: 'بازیابی',
    copied: 'کپی شد',
  },
  language: {
    title: 'زبانت رو انتخاب کن',
    subtitle: 'هر وقت خواستی می‌تونی از تنظیمات عوضش کنی.',
    continue: 'ادامه',
  },
  onboarding: {
    welcomeSubtitle: 'استودیو استیکر',
    enterSub: 'ساخت پک استیکر تلگرام',
    touchStatue: 'برای ورود، مجسمه رو لمس کن',
    channelPrompt: 'برای استفاده از Sticker Studio عضو شو',
    chooseLook: 'استودیوت رو شخصی‌سازی کن',
    colorTheme: 'تم',
    resetTheme: 'برگردوندن به تم پیش‌فرض Astral',
    appearance: 'ظاهر',
  },
  nav: {
    home: 'خانه',
    projects: 'پروژه‌ها',
    channels: 'کانال‌ها',
    settings: 'تنظیمات',
    admin: 'مدیریت',
  },

  guide: {
    title: 'راهنما',
    subtitle: 'هر چیزی که برای کار با Astral لازم داری.',
    searchPlaceholder: 'جستجو در راهنما…',
    noResults: 'چیزی پیدا نشد.',
    start: {
      title: 'شروع کار',
      whatIsT: 'Astral چیه؟',
      whatIsD:
        'با Astral می‌تونی عکس‌ها و ویدیو‌هات رو به پک استیکر تلگرام تبدیل کنی — ساخت، انتشار و به‌روزرسانی، همه توی همین مینی‌اپ.',
      createT: 'ساخت پروژه',
      createD:
        'روی «ساخت پک جدید» بزن، یه اسم بذار و نوعش رو انتخاب کن: عکسی، ویدیویی یا ترکیبی. هر پروژه در نهایت یه پک استیکر تلگرام می‌شه.',
      uploadT: 'اضافه کردن استیکر',
      uploadD:
        'فایل‌هات رو یکی‌یکی یا چندتایی آپلود کن. Astral خودش هرکدوم رو به فرمت و سایز مناسب تلگرام تبدیل می‌کنه.',
    },
    stickers: {
      title: 'استیکرها',
      processT: 'تبدیل خودکار',
      processD:
        'عکس رو آپلود کن؛ Astral اون رو داخل کادر ۵۱۲×۵۱۲ به WebP شفاف تبدیل می‌کنه. ویدیو هم به WebM کوتاه و سازگار با تلگرام تبدیل می‌شه. شفافیت عکس حفظ می‌مونه.',
      reorderT: 'تغییر ترتیب',
      reorderD:
        'یه استیکر رو نگه دار و بکش تا جابه‌جا بشه. همون ترتیبی که می‌بینی، توی تلگرام هم همونه.',
      replaceT: 'جایگزینی یا حذف',
      replaceD:
        'روی استیکر بزن تا پیش‌نمایشش رو ببینی، بعد فایلش رو عوض کن یا حذفش کن. اگه پک قبلاً منتشر شده، این تغییرات توی به‌روزرسانی بعدی اعمال می‌شن.',
    },
    emoji: {
      title: 'ایموجی',
      singleT: 'یک یا چند ایموجی',
      singleD:
        'هر استیکر حداقل به یه ایموجی نیاز داره. روی استیکر بزن تا عوضش کنی، یا چندتا ایموجی بذار (تا سقف تلگرام) که راحت‌تر پیدا بشه.',
      deviceT: 'هر ایموجی‌ای که بخوای',
      deviceD:
        'می‌تونی از انتخابگر خود اپ یا کیبورد ایموجی گوشیت استفاده کنی — پرچم‌ها، رنگ پوست، ایموجی‌های ترکیبی و کی‌کپ همه کار می‌کنن.',
      presetsT: 'پریست و ترتیب ایموجی',
      presetsD:
        'می‌تونی پریست ایموجی بسازی و بارها ازش استفاده کنی، یا یه ترتیب ایموجی رو پشت‌سرهم روی چندتا استیکر اعمال کنی — روی انتخاب‌شده‌ها یا کل پک.',
    },
    publish: {
      title: 'انتشار',
      linkT: 'انتخاب لینک پک',
      linkD:
        'یه لینک دلخواه برای پکت انتخاب کن. Astral همون لحظه چک می‌کنه که آزاد باشه و پسوند لازم «_by_AstralStickerBot» رو خودش اضافه می‌کنه.',
      progressT: 'مراحل انتشار',
      progressD:
        'انتشار توی پس‌زمینه انجام می‌شه و پیشرفتش رو می‌بینی. توی پک‌های بزرگ، آپلود استیکرها رو یکی‌یکی می‌بینی و آخرش لینک پک رو تحویل می‌گیری.',
      syncT: 'به‌روزرسانی پک منتشرشده',
      syncD:
        'پروژهٔ منتشرشده رو ویرایش کن و «بروزرسانی پک تلگرام» رو بزن تا اضافه‌ها، حذف‌ها، ایموجی‌ها و ترتیب همگام بشن — بدون اینکه پک تکراری ساخته بشه. آخر اسم پک‌های رایگان تگ ﹙@AstralStickerBot﹚ اضافه می‌شه.',
    },
    premium: {
      title: 'پرمیوم',
      freeT: 'محدودیت‌های پلن رایگان',
      freeD:
        'توی پلن رایگان، هر ماه تعداد مشخصی پک می‌تونی بسازی که به نوع پک بستگی داره. مالک و ادمین‌ها محدودیتی ندارن.',
      howT: 'چطور پرمیوم بگیرم؟',
      howD:
        'برو به بخش پرمیوم، مدت اشتراک رو انتخاب کن و بعد روش پرداخت رو مشخص کن. پرداخت به‌صورت امن توی ربات Astral انجام می‌شه.',
      methodsT: 'تومان یا Telegram Stars',
      methodsD:
        'می‌تونی تومانی پرداخت کنی و رسیدش رو برای بررسی بفرستی، یا با Telegram Stars پرداخت کنی که پرمیوم بلافاصله و خودکار فعال می‌شه.',
    },
    more: {
      title: 'بیشتر',
      channelsT: 'کانال‌های Astral',
      channelsD:
        'لیست کانال‌های رسمی Astral توی تلگرام. روی هرکدوم بزنی باز می‌شه. فقط ادمین‌ها می‌تونن کانال اضافه یا ویرایش کنن و این بخش هیچ تأثیری روی پک‌هات نداره.',
      appearanceT: 'زبان و ظاهر',
      appearanceD: 'بین English، فارسی و Русский جابه‌جا شو. زبان رو از تنظیمات عوض کن.',
      helpT: 'کمک می‌خوای؟',
      helpD: 'از طریق کانال رسمی که توی بخش کانال‌های Astral هست با ما در تماس باش.',
    },
  },
  home: {
    greeting: 'سلام {name}',
    heroKicker: 'Astral Studio',
    heroTitle: 'استودیو استیکر',
    heroSubtitle: 'عکس‌ها و ویدیو‌هات رو به پک استیکر تلگرام تبدیل کن.',
    createPack: 'ساخت پک جدید',
    createPackSub: 'از اینجا شروع کن',
    myProjects: 'پروژه‌های من',
    settings: 'تنظیمات',
    adminPanel: 'پنل مدیریت',
    quickActions: 'دسترسی سریع',
    recentProjects: 'پروژه‌های اخیر',
    viewAll: 'دیدن همه',
    noRecent: 'هنوز پروژه‌ای نساختی. اولین پکت رو بساز!',
    toolsTitle: 'ابزارهای Astral',
    tools: {
      projects: 'پروژه‌ها',
      projectsSub: 'پک‌های استیکرت',
      premium: 'پرمیوم',
      premiumSub: 'امکانات بیشتر',
      emojis: 'ایموجی‌ها',
      emojisSub: 'پریست‌های ایموجی',
      channels: 'کانال‌های Astral',
      channelsSub: 'کانال‌های رسمی',
      guide: 'راهنما',
      guideSub: 'آموزش کار با Astral',
      settings: 'تنظیمات',
      settingsSub: 'زبان و پشتیبانی',
      admin: 'مدیریت',
      adminSub: 'مدیریت کاربرها',
    },
    officialName: '@AstralAtelier',
    officialSub: 'ما رو توی تلگرام دنبال کن',
    introTitle: 'به Astral خوش اومدی ✦',
    introBody: 'اینجا می‌تونی عکس‌ها و ویدیو‌هات رو به پک استیکر تلگرام تبدیل کنی.',
    introCta: 'بریم به استودیو',
  },
  projects: {
    title: 'پروژه‌ها',
    newProject: 'پروژهٔ جدید',
    searchPlaceholder: 'جستجوی پروژه‌ها…',
    empty: 'هنوز پروژه‌ای نداری',
    emptyHint: 'برای شروع، اولین پروژه‌ت رو بساز.',
    emptySearch: 'پروژه‌ای با این جستجو پیدا نشد.',
    emptyArchived: 'پروژهٔ بایگانی‌شده‌ای نداری.',
    drafts: 'در حال ساخت',
    all: 'همه',
    lastEdited: 'ویرایش {date}',
    autosavedAt: 'ذخیرهٔ خودکار {date}',
    open: 'باز کردن',
    continueEditing: 'ادامه',
    overview: 'نمای کلی',
    category: 'دسته‌بندی',
    allCategories: 'همهٔ دسته‌بندی‌ها',
    stickerCount: '{count} استیکر',
    more: 'بیشتر',
    archive: 'بایگانی',
    restore: 'بازیابی',
    deleteConfirmTitle: 'حذف پروژه',
    deleteConfirmBody: '«{title}» حذف بشه؟ این کار قابل برگشت نیست.',
    archiveConfirmTitle: 'بایگانی پروژه',
    archiveConfirmBody: '«{title}» بایگانی بشه؟ هر وقت خواستی می‌تونی برش گردونی.',
    restoreConfirmTitle: 'بازیابی پروژه',
    restoreConfirmBody: '«{title}» به پروژه‌های فعال برگرده؟',
    status: {
      DRAFT: 'در حال ساخت',
      ACTIVE: 'فعال',
      ARCHIVED: 'بایگانی‌شده',
    },
  },
  projectForm: {
    createTitle: 'پروژهٔ جدید',
    editTitle: 'ویرایش پروژه',
    titleLabel: 'عنوان',
    titlePlaceholder: 'پک باحال من',
    descriptionLabel: 'توضیحات',
    descriptionPlaceholder: 'این پک درباره چیه؟',
    statusLabel: 'وضعیت',
    categoryLabel: 'دسته‌بندی',
    typeLabel: 'نوع پک',
    tagsLabel: 'برچسب‌ها',
    tagsPlaceholder: 'برچسب رو بنویس و Enter بزن',
    notesLabel: 'یادداشت‌ها',
    notesPlaceholder: 'ایده‌هات برای این پک رو اینجا بنویس…',
    create: 'ساخت پروژه',
    save: 'ذخیرهٔ تغییرات',
    historyTitle: 'تاریخچهٔ نسخه‌ها',
    history: 'تاریخچه',
    noHistory: 'هنوز تاریخچه‌ای نیست. تغییرات خودکار ذخیره می‌شن.',
    restore: 'بازیابی',
  },
  stickers: {
    failure: {
      unreadable: 'نتوانستیم این تصویر را بخوانیم.',
      tooLarge: 'این فایل بیش از حد بزرگ است.',
      tooDetailed: 'این تصویر برای استیکر شدن بیش از حد پرجزئیات است.',
      temporary: 'پردازش این تصویر ممکن نشد. دوباره تلاش کنید.',
      retrying: 'پردازش موقتاً ناموفق بود. در حال تلاش دوباره…',
      cancelled: 'بارگذاری لغو شد.',
    },
    title: 'استیکرها',
    count: '{count} استیکر',
    add: 'افزودن استیکر',
    empty: 'هنوز استیکری اضافه نشده',
    emptyHint: 'برای ساخت پک، اولین استیکرت رو اضافه کن.',
    remove: 'حذف استیکر',
    removeConfirmTitle: 'حذف استیکر',
    removeConfirmBody: 'این استیکر از پک حذف بشه؟',
    moveUp: 'انتقال به بالا',
    moveDown: 'انتقال به پایین',
    added: 'استیکر اضافه شد',
    removed: 'استیکر حذف شد',
    replaced: 'استیکر عوض شد',
    addHint: 'Astral خودش فایل رو به استیکر تلگرام تبدیل می‌کنه.',
    processing: 'در حال پردازش…',
    processFailed: 'این فایل پردازش نشد.',
    limitReached: 'به سقف استیکر این پک رسیدی.',
    errorReadFailed: 'نتونستیم این فایل رو بخونیم.',
    emoji: 'ایموجی',
    changeEmoji: 'تغییر ایموجی',
    emojiListLabel: 'ایموجی‌ها (۱ تا ۲۰)',
    emojiUpdated: 'ایموجی به‌روز شد',
    replace: 'جایگزینی',
    preview: 'پیش‌نمایش',
    select: 'انتخاب',
    selectAll: 'انتخاب همه',
    deselectAll: 'لغو انتخاب همه',
    range: 'محدوده',
    rangeArmed: 'حالا آخری رو انتخاب کن…',
    applyEmoji: 'اعمال ایموجی',
    editEmoji: 'ویرایش ایموجی',
    applySequence: 'اعمال ترتیب',
    selectedCount: '{count} مورد انتخاب شد',
    bulkTitle: 'ایموجی برای {count} استیکر',
    bulkSame: 'یکسان',
    bulkSameDesc: 'یه ایموجی رو روی همهٔ استیکرهای انتخاب‌شده بذار.',
    bulkSequence: 'ترتیب',
    bulkSequenceDesc: 'یه ترتیب ایموجی رو به‌صورت چرخشی روی استیکرهای انتخاب‌شده اعمال کن.',
    bulkPreset: 'پریست',
    bulkPresetDesc: 'یه پریست ذخیره‌شده رو روی استیکرهای انتخاب‌شده اعمال کن.',
  },
  emoji: {
    title: 'انتخاب ایموجی',
    searchPlaceholder: 'جستجوی ایموجی…',
    noResults: 'ایموجی‌ای پیدا نشد.',
    useKeyboard: 'استفاده از کیبورد گوشی',
    customPlaceholder: 'هر ایموجی‌ای رو تایپ یا پیست کن…',
    addEmoji: 'افزودن',
    invalid: 'این ایموجی معتبر نیست',
    categories: {
      recent: 'اخیر',
      smileys: 'شکلک‌ها',
      gestures: 'حرکات',
      people: 'آدم‌ها',
      animals: 'حیوانات',
      food: 'غذا',
      activity: 'فعالیت',
      travel: 'سفر',
      objects: 'اشیا',
      hearts: 'قلب‌ها',
      symbols: 'نمادها',
      flags: 'پرچم‌ها',
    },
  },

  presets: {
    title: 'پریست‌های ایموجی',
    create: 'پریست جدید',
    edit: 'ویرایش',
    name: 'اسم پریست',
    namePlaceholder: 'مثلاً بامزه',
    sequenceLabel: 'ترتیب ایموجی',
    empty: 'هنوز پریستی نساختی.',
    save: 'ذخیرهٔ پریست',
    saveAs: 'ذخیره به‌عنوان پریست',
    created: 'پریست ساخته شد',
    updated: 'پریست به‌روز شد',
    deleted: 'پریست حذف شد',
    saveFailed: 'پریست ذخیره نشد.',
    apply: 'اعمال',
    manage: 'مدیریت پریست‌ها',
    reorderHint: 'برای جابه‌جایی نگه دار و بکش',
    add: 'افزودن ایموجی',
    defaultLabel: 'ایموجی پیش‌فرض',
    singleDefault: 'پیش‌فرض تکی',
    autoApply: 'اعمال خودکار روی استیکرهای جدید',
    applyToAll: 'اعمال روی همهٔ استیکرها',
  },
  batch: {
    progress: '{done} از {total} پردازش شد',
    added: '{count} استیکر اضافه شد',
    failed: '{count} تا ناموفق',
    retry: 'تلاش دوباره ({count})',
    capacity: '{added} استیکر اضافه شد — به سقف پک رسیدی.',
  },
  stickerTypes: {
    IMAGE: 'عکسی',
    VIDEO: 'ویدیویی',
    MIXED: 'ترکیبی',
  },
  membership: {
    title: 'اول عضو Astral شو ✦',
    body: 'برای استفاده از Sticker Studio باید عضو {channel} باشی.',
    join: 'عضویت در کانال',
    checkAgain: 'عضو شدم، بررسی کن',
    unavailable: 'الان نتونستیم عضویتت رو بررسی کنیم. چند لحظه دیگه دوباره امتحان کن.',
    required: 'برای ساخت و انتشار پک باید عضو {channel} باشی.',
  },
  publish: {
    error: {
      syncInProgress: 'همین حالا داره این پک به‌روزرسانی می‌شه. یه لحظه صبر کن.',
      packFull: 'این پک پره. تلگرام حداکثر ۱۲۰ استیکر قبول می‌کنه — چندتا رو حذف کن و دوباره به‌روزرسانی کن.',
      packMissing: 'این پک دیگه توی تلگرام وجود نداره.',
      stickerRejected: 'تلگرام یکی از استیکرها رو قبول نکرد. سعی کن جایگزینش کنی.',
      mappingUnresolved: 'نتونستیم این پروژه رو به پک تلگرامیش وصل کنیم، برای همین چیزی تغییر نکرد. لطفاً با پشتیبانی تماس بگیر.',
      rateLimited: 'تلگرام الان شلوغه. چند لحظه دیگه دوباره امتحان کن — تغییرات تو ذخیره شده.',
      telegramUnavailable: 'تلگرام موقتاً در دسترس نیست. دوباره تلاش کن.',
      mediaMissing: 'فایل یکی از استیکرها خونده نشد. اون استیکر رو جایگزین کن.',
      unknown: 'موقع به‌روزرسانی پک مشکلی پیش اومد. دوباره تلاش کن.',
    },
    action: 'ساخت پک استیکر',
    publishing: 'در حال ساخت پک…',
    successTitle: 'پکت ساخته شد',
    open: 'باز کردن در تلگرام',
    openPack: 'باز کردن پک',
    copyLink: 'کپی لینک',
    needStickers: 'اول حداقل یه استیکر اضافه کن.',
    failed: 'پک ساخته نشد.',
    published: 'منتشرشده',
    count: '{count} استیکر',
    update: 'بروزرسانی پک تلگرام',
    updating: 'در حال بروزرسانی…',
    synced: 'همگام‌شده',
    localChanges: 'تغییرات منتشرنشده',
    syncFailed: 'همگام‌سازی ناموفق',
    syncDone: 'پک به‌روز شد',
    packMissing: 'پک توی تلگرام پیدا نشد',
    reviewTitle: 'بررسی پک',
    reviewCta: 'بررسی و انتشار',
    progressTitle: 'در حال انتشار',
    ready: 'پک استیکرت آماده‌ست ✨',
    retry: 'تلاش دوباره',
    failedAt: 'توی مرحلهٔ {phase} مشکل پیش اومد',
    phase: {
      PREPARING: 'آماده‌سازی فایل‌ها',
      CREATING_SET: 'ساخت پک',
      UPLOADING: 'آپلود استیکرها',
      FINALIZING: 'نهایی‌سازی',
    },
  },

  packName: {
    action: 'ویرایش نام پک',
    title: 'ویرایش نام پک',
    nameLabel: 'نام پک',
    currentLabel: 'عنوان فعلی در تلگرام',
    newLabel: 'عنوان جدید در تلگرام',
    trimmed: 'برای رعایت محدودیت ۶۴ نویسه‌ای تلگرام کوتاه شد.',
    hint: 'نام بلافاصله در تلگرام به‌روزرسانی می‌شود. لینک پک هرگز تغییر نمی‌کند.',
    saved: 'نام پک به‌روزرسانی شد',
    failed: 'به‌روزرسانی نام پک ممکن نشد.',
  },
  packLink: {
    title: 'لینک پک استیکر',
    nameLabel: 'اسم پک',
    branding: {
      label: 'تگ پک',
      none: 'بدون تگ',
      start: 'تگ اول',
      end: 'تگ آخر',
    },
    linkLabel: 'لینک پک',
    linkPlaceholder: 'cute_cats',
    checking: 'داریم چک می‌کنیم…',
    available: 'آزاده',
    taken: 'قبلاً گرفته شده',
    error: {
      empty: 'یه لینک بنویس',
      start: 'باید با حرف شروع بشه',
      chars: 'فقط حروف انگلیسی، عدد و زیرخط',
      underscore: 'زیرخط پشت‌سرهم یا در انتها نمی‌شه',
      tooLong: 'خیلی طولانیه',
    },
  },
  channels: {
    title: 'کانال‌های Astral',
    subtitle: 'کانال‌های رسمی Astral.',
    empty: 'هنوز کانالی اضافه نشده.',
    add: 'افزودن کانال',
    addTitle: 'افزودن کانال',
    editTitle: 'ویرایش کانال',
    usernameLabel: 'یوزرنیم کانال',
    usernamePlaceholder: '@channel',
    nameLabel: 'اسم',
    labelLabel: 'برچسب',
    descriptionLabel: 'توضیحات',
    descriptionPlaceholder: 'یه توضیح کوتاه',
    categoryLabel: 'دسته',
    open: 'باز کردن در تلگرام',
    manage: 'مدیریت',
    removeConfirmTitle: 'حذف کانال',
    removeConfirmBody: '{name} از کانال‌های Astral حذف بشه؟',
  },
  usage: {
    title: 'مصرف',
    plan: 'پلن',
    free: 'رایگان',
    premium: 'پرمیوم',
    packs: '{used} از {limit} پک',
    unlimited: 'نامحدود',
    remaining: '{count} تا باقی مونده',
    limitTitle: 'به سقف این ماه رسیدی',
    limitBody: 'سهمیهٔ رایگان پک‌های {type} این ماهت تموم شده.',
    upgrade: 'ارتقا به پرمیوم',
    premiumSoon: 'پرمیوم به‌زودی.',
  },
  categoryManager: {
    add: 'افزودن دسته‌بندی',
    manage: 'مدیریت دسته‌بندی‌ها',
    name: 'نام دسته‌بندی',
    create: 'ساختن',
    delete: 'حذف دسته‌بندی',
    deleteConfirm: 'این دسته‌بندی حذف بشه؟',
    deleteBody: 'پروژه‌های داخلش حذف نمی‌شن و بدون دسته‌بندی می‌مونن.',
    empty: 'هنوز دسته‌بندی‌ای نساختی.',
    none: 'بدون دسته‌بندی',
    created: 'دسته‌بندی اضافه شد',
    deleted: 'دسته‌بندی حذف شد',
    errorRequired: 'اسم دسته‌بندی رو وارد کن.',
    errorDuplicate: 'این دسته‌بندی از قبل وجود داره.',
    errorReserved: 'این دسته‌بندی از قبل وجود داره.',
    errorTooLong: 'این اسم خیلی بلنده.',
    errorFailed: 'ذخیرهٔ دسته‌بندی ممکن نشد.',
    legacy: 'بدون دسته‌بندی',
  },
  categories: {
    characters: 'شخصیت‌ها',
    movies: 'فیلم‌ها',
    shows: 'سریال‌ها',
    comics: 'کمیک‌ها',
    games: 'بازی‌ها',
    memes: 'میم‌ها',
  },
  settings: {
    title: 'تنظیمات',
    appearance: 'ظاهر',
    theme: 'تم',
    colorTheme: 'تم',
    themeMode: 'حالت نمایش',
    language: 'زبان',
    profile: 'پروفایل',
    account: 'حساب کاربری',
    notifications: 'اعلان‌ها',
    notificationsDesc: 'از وضعیت پروژه‌هات باخبرت می‌کنیم.',
    role: 'نقش',
    about: 'درباره',
    version: 'نسخه',
    themeModes: {
      dark: 'تیره',
      light: 'روشن',
      auto: 'خودکار',
      custom: 'سفارشی',
    },
  },
  admin: {
    title: 'پنل مدیریت',
    subtitle: 'تیم و اعضا رو مدیریت کن.',
    tabUsers: 'کاربرها',
    tabAdmins: 'ادمین‌ها',
    searchPlaceholder: 'جستجوی کاربر…',
    makeAdmin: 'ارتقا به ادمین',
    removeAdmin: 'حذف از ادمین‌ها',
    you: 'شما',
    noUsers: 'کاربری پیدا نشد.',
    memberSince: 'عضو از {date}',
    total: '{count} عضو',
    details: 'اطلاعات کاربر',
    telegramId: 'شناسهٔ تلگرام',
    viewProfile: 'دیدن پروفایل تلگرام',
    noProfile: 'یوزرنیم عمومی نداره',
    premium: 'پرمیوم',
    grantPremium: 'فعال‌سازی پرمیوم',
    revokePremium: 'لغو پرمیوم',
    copyId: 'کپی شناسه',
    premiumUntil: 'پرمیوم تا {date}',
    filterAll: 'همه',
    filterAttention: 'نیازمند رسیدگی',
    filterPremium: 'پرمیوم',
    filterPayments: 'پرداخت‌ها',
    filterAdmins: 'ادمین‌ها',
    attentionReceipt: 'رسید برای بررسی',
    reviewTitle: 'بررسی پرداخت',
    reviewOpen: 'باز کردن بررسی',
    queueEmpty: 'فعلاً چیزی برای بررسی نیست.',
    plan: 'پلن',
    price: 'قیمت',
    requested: 'تاریخ درخواست',
    receipt: 'رسید',
    viewReceipt: 'دیدن رسید',
    noReceipt: 'هنوز رسیدی نفرستاده',
    approve: 'تأیید',
    reject: 'رد',
    approveConfirm: 'این پرداخت تأیید و پرمیوم فعال بشه؟',
    rejectConfirm: 'این پرداخت رد بشه؟ پرمیوم فعال نمی‌شه.',
    rejectReason: 'دلیل (اختیاری)',
    approved: 'تأییدشده',
    rejected: 'ردشده',
    stAWAITING_RECEIPT: 'در انتظار رسید',
    stRECEIPT_SUBMITTED: 'نیازمند بررسی',
    stAPPROVED: 'تأییدشده',
    stREJECTED: 'ردشده',
    stEXPIRED: 'منقضی‌شده',
    stCANCELLED: 'لغوشده',
    stAWAITING_PAYMENT: 'در انتظار پرداخت',
    stCOMPLETED: 'تکمیل‌شده',
    stREFUNDED: 'بازپرداخت‌شده',
    stFAILED: 'ناموفق',
    actionDone: 'انجام شد',
    method: 'روش',
    methodToman: 'تومان / دستی',
    methodStars: 'Stars / خودکار',
    starsAmount: '{amount} Stars',
    chargeId: 'شناسهٔ تراکنش',
    filterHistory: 'تاریخچه',
  },
  roles: {
    OWNER: 'مالک',
    ADMIN: 'ادمین',
    USER: 'کاربر',
  },
  toast: {
    created: 'پروژه ساخته شد',
    archived: 'پروژه بایگانی شد',
    restored: 'پروژه برگشت',
    duplicated: 'کپی پروژه ساخته شد',
    deleted: 'پروژه حذف شد',
  },
  auth: {
    signingIn: 'در حال ورود…',
    notInTelegram: 'توی تلگرام بازش کن',
    notInTelegramHint: 'این اپ داخل تلگرام اجرا می‌شه. لطفاً از طریق ربات بازش کن.',
    failed: 'ورود انجام نشد',
    retry: 'تلاش دوباره',
  },
  errors: {
    generic: 'یه مشکلی پیش اومد.',
    network: 'مشکل از اتصاله. اینترنتت رو چک کن و دوباره امتحان کن.',
    unauthorized: 'نشستت منقضی شده. دوباره وارد شو.',
    notFound: 'پیدا نشد.',
    loadFailed: 'بارگذاری نشد. دوباره تلاش کن.',
  },

  premium: {
    title: 'پرمیوم Astral',
    active: 'فعال',
    free: 'پلن رایگان',
    statusFree: 'الان روی پلن رایگانی',
    statusActive: 'پرمیوم Astral فعاله',
    expires: 'تا {date}',
    daysLeft: '{count} روز مونده',
    homeTitle: 'پرمیوم Astral',
    homeBody: 'پک بیشتر، سقف بالاتر.',
    view: 'دیدن پرمیوم',
    renew: 'تمدید',
    upgradeHint: 'با پرمیوم Astral می‌تونی پک‌های بیشتری بسازی و سقف استیکرت بالاتر می‌ره.',
    unlimitedPacks: 'پک نامحدود در ماه',
    maxCapacityShort: 'بیشترین ظرفیت',
    tagIncluded: 'دارای تگ ﹙@AstralStickerBot﹚',
    tagNone: 'بدون تگ Astral',
    starsUnit: 'Stars',
    perPackTotal: 'مجموعاً {count} در هر پک',
    mixedNote: 'مجموع عکس و ویدیو',
    compareTitle: 'چی دریافت می‌کنی',
    planFree: 'رایگان',
    planMonth1: '۱ ماهه',
    planMonth3: '۳ ماهه',
    perPack: '{count} در هر پک',
    maxCapacity: 'بیشترین ظرفیت ممکن',
    subscribe: 'خرید اشتراک · {price}',
    tomanAmount: '{amount} تومان',
    imagePacks: 'پک‌های عکسی',
    videoPacks: 'پک‌های ویدیویی',
    mixedPacks: 'پک‌های ترکیبی',
    capacity: 'استیکر در هر پک',
    plansTitle: 'یه پلن انتخاب کن',
    bestValue: 'به‌صرفه‌ترین',
    save: '{percent}٪ تخفیف',
    month1: '۱ ماهه',
    month3: '۳ ماهه',
    month12: '۱۲ ماهه',
    perksTitle: 'پرمیوم چی بهت می‌ده',
    perkPacks: 'پک استیکر نامحدود توی هر ماه',
    perkStickers: 'تا ۱۲۰ استیکر در هر پک، برای همهٔ نوع‌ها',
    freeLimitsTitle: 'سقف پلن رایگانت',
    premiumLimitsTitle: 'با پرمیوم',
    unlimited: 'نامحدود',
    packsPerMonth: '{count} پک در ماه',
    continueInBot: 'ادامه در تلگرام',
    purchaseNote: 'پرداخت به‌صورت امن توی ربات Astral انجام می‌شه.',
    methodTitle: 'روش پرداخت',
    methodToman: 'پرداخت تومانی',
    methodTomanSub: 'دستی · با بررسی رسید',
    methodStars: 'Telegram Stars',
    methodStarsSub: 'فوری · فعال‌سازی خودکار',
    toman: 'تومان',
    payStars: 'پرداخت با {amount} Stars',
    continueStars: 'ادامه برای پرداخت با Stars',
    selectPlan: 'یه پلن انتخاب کن',
    pendingTitle: 'پرداخت در حال بررسی',
    pendingBody: 'رسیدت در حال بررسیه. به‌محض تأیید، پرمیوم فعال می‌شه.',
    awaitingTitle: 'توی ربات تکمیلش کن',
    awaitingBody: 'برای فرستادن رسید پرداخت، ربات رو باز کن.',
    loadFailed: 'اطلاعات پرمیوم لود نشد.',
  },

  premiumNotify: {
    receiptReceived: 'رسیدت دریافت شد ✅ یکی از ادمین‌های Astral بررسیش می‌کنه. به‌محض تأیید، پرمیوم فعال می‌شه.',
    approvedTitle: '✨ پرمیوم Astral فعال شد!',
    approvedBody: 'پرمیوم {months} ماههٔ تو فعاله و تا {date} اعتبار داره.',
    openApp: 'باز کردن Astral Studio',
    rejectedTitle: 'رسید تأیید نشد',
    rejectedBody: 'رسید پرداختت تأیید نشد، برای همین پرمیوم فعال نشد.',
    rejectedReason: 'دلیل: {reason}',
    tryAgain: 'تلاش دوباره',
  },

  premiumBot: {
    intro: '✨ با پرمیوم Astral پک بیشتری می‌سازی و سقف استیکرت بالاتر می‌ره. یه پلن انتخاب کن:',
    chosen: 'پرمیوم Astral — {plan}',
    chooseMethod: 'روش پرداخت رو انتخاب کن:',
    methodToman: 'تومان · {amount}',
    methodStars: 'Telegram Stars · {amount}',
    toman: 'تومان',
    invoiceTitle: 'پرمیوم Astral — {plan}',
    invoiceDesc: 'اشتراک پرمیوم Astral ({plan}). فعال‌سازی فوری و خودکار.',
    invoiceFailed: 'پرداخت با Stars شروع نشد. لطفاً دوباره /premium رو بزن.',
    planPick: 'پرمیوم Astral — {plan} با قیمت {price} {currency}.',
    windowNote:
      'وقتی آماده بودی، روی دکمهٔ پرداخت بزن. بعد از شروع، {minutes} دقیقه وقت داری رسید رو بفرستی. اگه منقضی شد، کافیه دوباره شروع کنی.',
    ready: 'آمادهٔ پرداختم',
    back: 'بازگشت',
    plansAgain: 'دیدن پلن‌ها',
    sessionTitle: '💳 پرداخت شروع شد — {plan} ({price} {currency})',
    paymentTitle: 'اطلاعات پرداخت:',
    cardNumber: 'شماره کارت: {value}',
    cardHolder: 'به نام: {value}',
    bankName: 'بانک: {value}',
    tomanUnavailable:
      'پرداخت کارتی موقتاً در دسترس نیست. می‌تونی با Stars پرداخت کنی یا بعداً دوباره امتحان کنی.',
    expiresAt: '{minutes} دقیقه وقت داری پرداخت رو انجام بدی و رسیدت رو بفرستی.',
    submit: 'ارسال رسید',
    cancel: 'لغو',
    sendReceipt: 'حالا رسید پرداخت رو بفرست — عکس یا فایل PDF.',
    receiptConfirmed: 'رسیدت دریافت شد ✅ به‌زودی یکی از ادمین‌ها بررسیش می‌کنه. بعد از تأیید، پرمیوم فعال می‌شه.',
    cancelled: 'درخواست پرداخت لغو شد. هر وقت خواستی با /premium دوباره شروع کن.',
    expired: 'این جلسهٔ پرداخت منقضی شده. با /premium یکی جدید شروع کن.',
    noActive: 'الان پرداخت فعالی نداری. با /premium شروع کن.',
    notImage: 'رسید باید عکس یا فایل PDF باشه.',
    open: 'باز کردن Astral Studio',
  },
  bot: {
    welcomeTitle: '✦ Astral Studio',
    welcomeBody: 'اینجا می‌تونی عکس‌ها و ویدیوهات رو راحت به پک استیکر تلگرام تبدیل کنی.',
    startHint: 'برای شروع، روی «Open App» بزن.',
    chooseLanguage: 'زبان ربات رو انتخاب کن',
    menuButton: 'باز کردن اپ',
    premiumInfo:
      '✦ پرمیوم Astral\n\n' +
      'پلن و روش پرداخت رو داخل Mini App انتخاب کن.\n' +
      'برای تکمیل پرداخت، دوباره به همین بات برمی‌گردی:\n\n' +
      '⭐ Stars — پرداخت داخل بات\n' +
      '💳 تومان — دریافت اطلاعات کارت و ارسال رسید داخل بات\n\n' +
      'برای شروع، Mini App رو باز کن و برو به بخش پرمیوم.',
    help: {
      title: '✦ راهنمای Astral Sticker Studio',
      botTitle: '🤖 توی این بات',
      botBody:
        '/start — انتخاب زبان و باز کردن اپ\n' +
        '/premium — پرمیوم Astral چطور کار می‌کنه\n' +
        '/help — همین راهنما\n' +
        'دکمهٔ منو کنار کادر پیام هم Sticker Studio رو باز می‌کنه.',
      payTitle: '⭐ پرداخت همین‌جا توی بات انجام می‌شه',
      payBody:
        'پلن رو داخل Mini App انتخاب کن، بعد برگرد اینجا:\n' +
        '• Stars — فاکتوری که بات می‌فرسته رو پرداخت کن\n' +
        '• تومان — بات اطلاعات کارت رو می‌فرسته؛ رسیدت رو همین‌جا بفرست',
      appTitle: '✨ داخل Mini App',
      appBody:
        '• پک جدید — تصویری، ویدیویی یا ترکیبی\n' +
        '• پروژه‌ها — همهٔ پک‌هات، با جست‌وجو و فیلتر\n' +
        '• افزودن استیکر — عکس یا ویدیو آپلود کن؛ خودکار تبدیل می‌شه\n' +
        '• جابه‌جایی — روی استیکر نگه دار و بکشش\n' +
        '• ایموجی — روی استیکر بزن تا ایموجیش عوض بشه، یا با «انتخاب» چندتا رو با هم تغییر بده\n' +
        '• انتشار — لینک پک (t.me/addstickers/…) رو انتخاب کن و پک رو بساز\n' +
        '• ویرایش نام پک — اسم پک منتشرشده رو هر وقت خواستی عوض کن\n' +
        '• تگ پک — پرمیوم، ادمین و اونر می‌تونن تگ ﹙@AstralStickerBot﹚ رو جابه‌جا یا حذف کنن\n' +
        '• به‌روزرسانی پک — بعد از ویرایش، تغییرات رو به تلگرام بفرست\n' +
        '• پرمیوم، تنظیمات، کانال‌های Astral و راهنما توی صفحهٔ خانه هستن',
      staffTitle: '🛡 برای ادمین‌ها',
      staffBody: 'از صفحهٔ خانه وارد بخش ادمین شو تا رسیدهای پرمیوم رو بررسی کنی و کاربرها رو مدیریت کنی.',
      channel: '📣 @AstralAtelier — اخبار و پک‌های جدید',
    },
  },
};
